import { useState, useMemo } from 'react'
import { router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Combobox } from '@/components/ui/combobox'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { FieldInputs, validateFields } from '@/components/applicant/FieldInputs'
import { Check, ArrowRight, Clock, DollarSign } from 'lucide-react'

type BusinessOption = { id: string; name: string; country_id: string | null; state_id: string | null }
type TypeOption = {
  id: string; code: string; name: string; jurisdiction_code: string;
  country_id: string | null; state_id: string | null;
  gov_fee_cents: number; service_fee_cents: number; total_cents: number;
  min_duration_months: number; processing_days_min: number | null; processing_days_max: number | null;
  fields: any[]
}

type Props = {
  countries: { id: string; code: string; name: string }[]
  states: { id: string; code: string; name: string; country_id: string }[]
  types: TypeOption[]
  businesses: BusinessOption[]
}

export default function RegisteredAddressNew({ countries, states, types, businesses }: Props) {
  const basePath = useBasePath()
  const [selectedCountryId, setSelectedCountryId] = useState('')
  const [selectedStateId, setSelectedStateId] = useState('')
  const [selectedBusinessId, setSelectedBusinessId] = useState('')
  const [selectedTypeId, setSelectedTypeId] = useState('')
  const [durationMonths, setDurationMonths] = useState(12)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fieldValues, setFieldValues] = useState<Record<string, string | number | string[]>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const countryStates = states.filter(s => s.country_id === selectedCountryId)
  const hasStates = countryStates.length > 0

  const availableTypes = useMemo(() => {
    if (!selectedCountryId) return []
    return types.filter(t => {
      if (t.country_id !== selectedCountryId) return false
      if (t.state_id) {
        if (!selectedStateId) return false
        if (t.state_id !== selectedStateId) return false
      }
      return true
    })
  }, [types, selectedCountryId, selectedStateId])
  const selectedType = types.find(t => t.id === selectedTypeId)
  const minDuration = selectedType?.min_duration_months || 12
  const durationOptions = useMemo(() => {
    const options = []
    for (let months = minDuration; months <= 24; months += 3) {
      const years = Math.floor(months / 12)
      const rem = months % 12
      let label = ''
      if (years > 0) label += `${years} year${years > 1 ? 's' : ''}`
      if (rem > 0) label += `${years > 0 ? ' ' : ''}${rem} month${rem > 1 ? 's' : ''}`
      options.push({ value: months, label })
    }
    return options
  }, [minDuration])

  const handleSubmit = () => {
    if (!selectedBusinessId || !selectedTypeId || !selectedType) return
    const errors = validateFields(selectedType.fields, fieldValues)
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return }
    setIsSubmitting(true)
    router.post(`${basePath}/registered_addresses`, {
      registered_address: {
        business_id: selectedBusinessId,
        registered_address_type_id: selectedTypeId,
        duration_months: durationMonths,
        field_values: fieldValues
      }
    }, { onFinish: () => setIsSubmitting(false) })
  }

  const totalFee = selectedType ? (selectedType.service_fee_cents + selectedType.gov_fee_cents) * durationMonths / 12 : 0

  return (
    <DashboardLayout title="New Registered Address Service" breadcrumbs={[{ label: 'Registered Address', href: 'registered_addresses' }, { label: 'New Service' }]}>
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">New Registered Address Service</h1>
        <p className="text-sm text-muted-foreground">Choose your business and service type</p>
      </div>
      <div className="space-y-6">
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">1</span>
            <h2 className="text-base font-medium text-foreground">Business</h2>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Select business</Label>
            <Combobox
              options={businesses.map(b => ({ value: b.id, label: b.name }))}
              value={selectedBusinessId}
              onValueChange={(value) => {
                setSelectedBusinessId(value)
                const biz = businesses.find(b => b.id === value)
                if (biz?.country_id) {
                  setSelectedCountryId(biz.country_id)
                  setSelectedStateId(biz.state_id || '')
                } else {
                  setSelectedCountryId('')
                  setSelectedStateId('')
                }
                setSelectedTypeId('')
              }}
              placeholder="Select a business..."
              searchPlaceholder="Search businesses..."
              emptyMessage="No businesses found."
            />
          </div>
        </section>

        {/* Jurisdiction */}
        {selectedBusinessId && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">2</span>
              <h2 className="text-base font-medium text-foreground">Jurisdiction</h2>
            </div>
            {(() => {
              const biz = businesses.find(b => b.id === selectedBusinessId)
              if (biz?.country_id) {
                const country = countries.find(c => c.id === biz.country_id)
                const state = states.find(s => s.id === biz.state_id)
                return (
                  <div className="px-3 py-2.5 bg-muted rounded-lg text-sm">
                    <span className="text-muted-foreground">Auto-selected: </span>
                    <span className="font-medium">{[state?.name, country?.name].filter(Boolean).join(', ')}</span>
                  </div>
                )
              }
              return (
                <div className={`grid gap-3 ${hasStates ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Country</Label>
                    <Combobox
                      options={countries.map(c => ({ value: c.id, label: c.name }))}
                      value={selectedCountryId}
                      onValueChange={(v) => { setSelectedCountryId(v); setSelectedStateId(''); setSelectedTypeId('') }}
                      placeholder="Select country..."
                      searchPlaceholder="Search countries..."
                      emptyMessage="No countries found."
                    />
                  </div>
                  {hasStates && (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">State</Label>
                      <Combobox
                        options={countryStates.map(s => ({ value: s.id, label: s.name }))}
                        value={selectedStateId}
                        onValueChange={(v) => { setSelectedStateId(v); setSelectedTypeId('') }}
                        placeholder="Select state..."
                        searchPlaceholder="Search states..."
                        emptyMessage="No states found."
                      />
                    </div>
                  )}
                </div>
              )
            })()}
          </section>
        )}

        {availableTypes.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">3</span>
              <h2 className="text-base font-medium text-foreground">Service Type</h2>
            </div>
            <div className="space-y-1.5">
              {availableTypes.map((t) => {
                const isSelected = selectedTypeId === t.id
                return (
                  <button key={t.id} type="button"
                    onClick={() => { setSelectedTypeId(t.id); setFieldValues({}); setFieldErrors({}) }}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${isSelected ? 'border-foreground bg-muted' : 'border-border hover:border-border'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{t.name}</span>
                          {isSelected && <Check className="w-4 h-4 text-foreground" />}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          {t.processing_days_min && (
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{t.processing_days_min}-{t.processing_days_max} days</span>
                          )}
                          <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{formatCurrency(t.total_cents)}/yr</span>
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        {selectedType && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">4</span>
              <h2 className="text-base font-medium text-foreground">Duration</h2>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Service duration</Label>
              <Combobox
                options={durationOptions.map(o => ({ value: o.value.toString(), label: o.label }))}
                value={durationMonths.toString()}
                onValueChange={(v) => setDurationMonths(parseInt(v))}
                placeholder="Select duration"
                searchPlaceholder="Search duration..."
                emptyMessage="No options."
              />
              <p className="text-xs text-muted-foreground">Minimum duration: {minDuration} months</p>
            </div>
          </section>
        )}

        {selectedType && selectedType.fields.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">5</span>
              <h2 className="text-base font-medium text-foreground">Additional Information</h2>
            </div>
            <FieldInputs
              fields={selectedType.fields}
              values={fieldValues}
              onChange={(fieldId, value) => setFieldValues(prev => ({ ...prev, [fieldId]: value }))}
              errors={fieldErrors}
            />
          </section>
        )}

        {selectedType && selectedBusinessId && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">{selectedType.fields.length > 0 ? '6' : '5'}</span>
              <h2 className="text-base font-medium text-foreground">Review & Submit</h2>
            </div>
            <div className="bg-muted rounded-xl p-4 space-y-3">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{selectedType.name} ({durationMonths} months)</span>
                  <span className="font-medium">{formatCurrency(totalFee)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground pl-4 text-xs">
                  <span>Service Fee</span>
                  <span>{formatCurrency(selectedType.service_fee_cents * durationMonths / 12)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground pl-4 text-xs">
                  <span>Government Fee</span>
                  <span>{formatCurrency(selectedType.gov_fee_cents * durationMonths / 12)}</span>
                </div>
                <div className="h-px bg-border" />
                <div className="flex justify-between font-medium">
                  <span>Total</span>
                  <span>{formatCurrency(totalFee)}</span>
                </div>
              </div>
              <Button onClick={handleSubmit} className="w-full h-9 text-sm" disabled={!selectedBusinessId || !selectedTypeId || isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Continue'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  )
}
